import { CoachOnboardingRow, CoachOnboardingBriefing } from '../../types/opsBriefing';

/**
 * 시안 `OPS_BRIEFING_ONBOARDING_UI.html` 의 **검산표를 그대로 옮긴 픽스처**.
 *
 * ─────────────────────────────────────────────────────────────
 * 시안 주석의 검산표 (원문)
 * ─────────────────────────────────────────────────────────────
 *   회원(정식) 15 = 김수산나3 + 김정원2 + 손수민4 + 정다은3 + 변용성3
 *   신규기회 1 (고은채·손수민) — 🔴 모든 분모에서 제외. 수강권 없음이 정상
 *   앱 다운 안 함 2 = 김수산나담당1 + 김용식1
 *   수강권 없음 6 = 김수산나2 + 김정원1 + 손수민3
 *   예약 없음 2 = 허지민 + 양문선 (수강권 보유·수락된 강사 담당만 셈)
 *   분석 시작 5 = 박인혜1 + 차현서1 + 변용성담당3 (스케줄 잡힌 회원)
 *   세팅 2/5 = 강사 등록✓ · 배정✓ / 수락✗ · 수강권✗ · 첫예약✗
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 왜 픽스처를 소스에 두나 (테스트 파일이 아니라)
 * ─────────────────────────────────────────────────────────────
 * 두 곳이 쓴다 — 검산 테스트와 **목 모드 화면**이다. 백엔드가 이 계약을
 * 병렬 구현 중이라, 서버가 붙기 전까지 개발·테섭에서 이 데이터로 화면을
 * 본다. 테스트에만 두면 화면 확인용 데이터를 따로 또 만들게 되고,
 * 그 순간 **시안과 다른 숫자로 화면을 검토**하게 된다.
 *
 * ⚠️ 이 값을 고치면 시안 검산표와 어긋난다. 고치지 말고, 다른 상황이
 *    필요하면 테스트에서 부분 덮어쓰기(`{...row, ...}`)로 만들어라.
 *
 * ⚠️ 이 시나리오에는 **만료 회원이 없다.** 그래서 `memberWithActivePass`
 *    (지금 활성)와 `memberWithPass`(등록 이력, 만료 포함)가 같은 값이다.
 *    QA6 로 두 뜻이 갈린 뒤에도 검산표가 그대로 성립하도록 **둘 다 적어 둔다** —
 *    안 적으면 폴백을 타서 「우연히 맞는」 상태가 되고, 회귀를 못 잡는다.
 */

/** 시안 기준일 — 경과일(12일째·9일째)이 이 날짜에서 나온다 */
export const FIXTURE_DATE = '2026-09-01';

const row = (r: CoachOnboardingRow): CoachOnboardingRow => r;

/** ① 김수산나 — 미수락 12일째 (8/20 등록) */
export const 김수산나 = row({
    coachId: 'c-susanna',
    name: '김수산나',
    phone: '010-4183-7729',
    registeredOn: '2026-08-20',
    isConnected: false,
    connectedOn: null,
    memberCount: 3,
    memberAppLinked: 2, // 다운 안 함 1
    memberWithPass: 1, // 없음 2
    memberWithActivePass: 1,
    /* 미수락이라 예약·수업·일지는 잠긴다 — 서버도 못 재므로 null */
    memberWithBooking: null,
    weekLessons: null,
    journal7d: null,
    opportunityCount: 0,
    memberIssues: [],
});

/** ② 김정원 — 미수락 9일째 (8/23 등록) */
export const 김정원 = row({
    coachId: 'c-jungwon',
    name: '김정원',
    phone: '010-2274-9166',
    registeredOn: '2026-08-23',
    isConnected: false,
    connectedOn: null,
    memberCount: 2,
    memberAppLinked: 2, // 전원 다운
    memberWithPass: 1, // 없음 1
    memberWithActivePass: 1,
    memberWithBooking: null,
    weekLessons: null,
    journal7d: null,
    opportunityCount: 0,
    memberIssues: [],
});

/** ③ 손수민 — 수락 완료, 수강권 3명 없음 + 신규기회 1명 */
export const 손수민 = row({
    coachId: 'c-sumin',
    name: '손수민',
    phone: '010-0000-0003',
    registeredOn: '2026-08-19',
    isConnected: true,
    connectedOn: '2026-08-21',
    memberCount: 4,
    memberAppLinked: 3, // 다운 안 함 1 (김용식)
    memberWithPass: 1, // 없음 3
    memberWithActivePass: 1,
    memberWithBooking: 1, // 수강권 보유 1명은 예약 있음 → 예약 없음 0
    weekLessons: 2,
    journal7d: { written: 1, total: 2 }, // 안 쓴 1건
    opportunityCount: 1, // 고은채
    memberIssues: [
        {
            memberId: 'm-yunjin',
            name: '윤진',
            kind: 'registered',
            needs: ['PASS'],
            note: '8/25 등록 · 앱 사용 중 · 수강권이 없어 예약 불가',
        },
        {
            memberId: 'm-yongsik',
            name: '김용식',
            kind: 'registered',
            needs: ['PASS', 'APP'],
            note: '8/26 등록 · 앱 다운 안 함 · 수강권이 없어 예약 불가',
        },
        {
            memberId: 'm-sanha',
            name: '박산하',
            kind: 'registered',
            needs: ['PASS'],
            note: '8/28 등록 · 앱 사용 중 · 수강권이 없어 예약 불가',
        },
        {
            memberId: 'm-eunchae',
            name: '고은채',
            kind: 'opportunity',
            needs: ['SUGGEST_PASS'],
            note: '신규기회 · 체험 수업 9/3 예정 — 수강권이 없는 게 정상입니다',
        },
    ],
});

/** ④ 정다은 — 수강권은 끝, 예약 2명 없음 */
export const 정다은 = row({
    coachId: 'c-daeun',
    name: '정다은',
    phone: '010-0000-0004',
    registeredOn: '2026-08-19',
    isConnected: true,
    connectedOn: '2026-08-22',
    memberCount: 3,
    memberAppLinked: 3,
    memberWithPass: 3,
    memberWithActivePass: 3,
    memberWithBooking: 1, // 예약 없음 2 (허지민·양문선)
    weekLessons: 1,
    journal7d: { written: 1, total: 2 },
    opportunityCount: 0,
    memberIssues: [
        {
            memberId: 'm-jimin',
            name: '허지민',
            kind: 'registered',
            needs: ['BOOKING'],
            note: '수강권 10회 남음 · 예약 없음 6일째',
        },
        {
            memberId: 'm-munseon',
            name: '양문선',
            kind: 'registered',
            needs: ['BOOKING'],
            note: '수강권 20회 남음 · 예약 없음 4일째',
        },
        {
            memberId: 'm-hyeonseo',
            name: '차현서',
            kind: 'registered',
            needs: ['JOURNAL'],
            note: '8/30 수업 완료 · 일지 없음',
        },
    ],
});

/** ⑤ 변용성 — 순항 */
export const 변용성 = row({
    coachId: 'c-yongseong',
    name: '변용성',
    phone: '010-0000-0005',
    registeredOn: '2026-08-18',
    isConnected: true,
    connectedOn: '2026-08-20',
    memberCount: 3,
    memberAppLinked: 3,
    memberWithPass: 3,
    memberWithActivePass: 3,
    memberWithBooking: 3,
    weekLessons: 5,
    journal7d: { written: 4, total: 4 },
    opportunityCount: 0,
    memberIssues: [],
});

export const FIXTURE_COACHES: CoachOnboardingRow[] = [
    김수산나,
    김정원,
    손수민,
    정다은,
    변용성,
];

/** 화면 전체 응답 — 히어로 값은 서버가 주는 것이라 검산표에서 그대로 옮긴다 */
export const FIXTURE_BRIEFING: CoachOnboardingBriefing = {
    date: FIXTURE_DATE,
    setupSteps: { done: 2, total: 5 },
    blockedAt: '강사 연결 수락 2명',
    /* 분석 시작 5 / 정식 회원 15 */
    membersAnalyzing: { count: 5, total: 15 },
    coaches: FIXTURE_COACHES,
    actions: [],
};
